"use client";

import { useRef, useState } from "react";
import {
  Checkbox,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  HINT,
  LABEL,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { QrDetails } from "@/components/tools/cipher-machines/qr-details";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { downloadPng, downloadSvg } from "@/lib/tool-kit/download";
import {
  capitalsFit,
  checkColors,
  hasScheme,
  type QrContentKind,
  qrPath,
  qrPayload,
  STANDARD_QUIET_ZONE,
} from "@/lib/tool-kit/qr/qr-output";
import {
  encodeText,
  QR_ECC_LEVELS,
  type QrEcc,
} from "@/lib/tool-kit/qr/qrcodegen";

const MASKS = ["auto", "0", "1", "2", "3", "4", "5", "6", "7"] as const;
type MaskChoice = (typeof MASKS)[number];
const MARGINS = ["0", "1", "2", "4", "6", "8"] as const;
type Margin = (typeof MARGINS)[number];
const PNG_TARGETS = ["300", "600", "1200", "2400"] as const;
type PngTarget = (typeof PNG_TARGETS)[number];

/** English strings. A translated content file overrides these through `ui`. */
export const QR_CODE_GENERATOR_STRINGS = {
  kind: "What should the code hold?",
  kindText: "Text",
  kindUrl: "Web address",
  kindDoi: "DOI",
  text: "Text",
  textSample: "Poster 14 · Data, code and slides: see the project page.",
  url: "Web address",
  urlSample: "https://www.example.org/poster-14",
  urlNoScheme:
    "This does not start with https:// (or another scheme such as mailto:), so most phone cameras will show it as plain text instead of opening it.",
  doi: "DOI",
  doiSample: "10.1038/171737a0",
  doiHint:
    "Paste a DOI as 10.xxxx/…, doi:10.xxxx/… or a doi.org link. The code holds the full https://doi.org/ link, so any phone camera opens the article.",
  doiInvalid:
    "That does not look like a DOI: a DOI starts with 10., then a registrant code of four or more digits, a slash and a suffix.",
  capitals: "Write the link in capitals for a smaller code",
  capitalsHint:
    "DOIs and the doi.org address are case-insensitive, so the link still works — and capitals fit QR's compact alphanumeric mode.",
  capitalsUnavailable:
    "This DOI contains characters outside QR's alphanumeric set (such as parentheses or underscores), so capitals would not make the code smaller.",
  holds: "The code holds:",
  ecc: "Error correction",
  eccL: "L — about 7% can be restored",
  eccM: "M — about 15%",
  eccQ: "Q — about 25%",
  eccH: "H — about 30%",
  eccHint:
    "How much of the code can be dirty, torn or covered and still read. A higher level makes a bigger code for the same data.",
  mask: "Mask pattern",
  maskAuto: "Automatic — lowest penalty",
  maskNumber: "Mask {number}",
  margin: "Quiet zone (modules)",
  marginHint: "The light border around the code. The standard asks for 4.",
  marginWarning:
    "A quiet zone narrower than 4 modules can stop some phones from finding the code — keep the space clear when you place it, too.",
  pngSize: "PNG size",
  pngOption: "{size} × {size} px",
  dark: "Modules",
  light: "Background",
  lowContrast:
    "The contrast between these colours is only {ratio}:1. Scanners need strong contrast — dark modules on a light background, at least 4:1.",
  inverted:
    "The modules are lighter than the background. Many scanners expect dark on light and cannot read an inverted code.",
  empty: "Type something to make a code.",
  tooLong:
    "Too long for a QR code at level {level}. Shorten the text or choose a lower error-correction level.",
  result: "Your QR code",
  imageLabel: "QR code for {payload}",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  exportFailed:
    "The PNG could not be made in this browser. Download the SVG instead.",
  fileName: "qr-code",
  scanNote:
    "Test the printed size with two or three phones before it goes on a poster or a slide.",
  detailsHeading: "What is inside",
  version: "Version",
  versionValue: "{version} — {size} × {size} modules",
  mode: "Encoding mode",
  modeNumeric: "Numeric",
  modeAlphanumeric: "Alphanumeric",
  modeByte: "Byte (UTF-8)",
  eccUsed: "Error correction",
  eccRaised:
    "Raised from {chosen} to {used} at no cost: the data still fits the same version.",
  dataUsed: "Data bits used",
  dataUsedValue: "{used} of {capacity}",
  blocks: "Reed–Solomon blocks",
  blocksValue: "{blocks} × {ecc} correction codewords",
  maskUsed: "Mask",
  penaltiesHeading: "Mask penalties",
  penaltiesHint:
    "Each of the eight masks is scored for patterns that confuse scanners — long runs of one colour, 2 × 2 blocks, finder-like stripes and dark/light imbalance. The lowest score wins.",
  penaltyMask: "Mask",
  penaltyScore: "Penalty",
  chosen: "chosen",
} as const;

export type QrStrings = typeof QR_CODE_GENERATOR_STRINGS;

const ECC_LABELS: Record<QrEcc, keyof QrStrings> = {
  L: "eccL",
  M: "eccM",
  Q: "eccQ",
  H: "eccH",
};

function ContentFields({
  kind,
  values,
  onValue,
  capitals,
  onCapitals,
  strings,
}: {
  kind: QrContentKind;
  values: Record<QrContentKind, string>;
  onValue: (kind: QrContentKind, value: string) => void;
  capitals: boolean;
  onCapitals: (value: boolean) => void;
  strings: QrStrings;
}) {
  if (kind === "text") {
    return (
      <TextAreaField
        id="qr-text"
        label={strings.text}
        onChange={(value) => onValue("text", value)}
        prose
        rows={4}
        value={values.text}
      />
    );
  }
  if (kind === "url") {
    const url = values.url.trim();
    return (
      <div className="space-y-2">
        <TextField
          id="qr-url"
          label={strings.url}
          mono
          onChange={(value) => onValue("url", value)}
          type="url"
          value={values.url}
        />
        {url !== "" && !hasScheme(url) ? (
          <Notice>{strings.urlNoScheme}</Notice>
        ) : null}
      </div>
    );
  }
  const payload = qrPayload("doi", values.doi, false);
  const fits = payload.ok && capitalsFit(payload.text);
  return (
    <div className="space-y-3">
      <TextField
        hint={strings.doiHint}
        id="qr-doi"
        label={strings.doi}
        mono
        onChange={(value) => onValue("doi", value)}
        value={values.doi}
      />
      {payload.ok ? (
        <div className="space-y-1">
          <Checkbox
            checked={capitals && fits}
            label={strings.capitals}
            onChange={onCapitals}
          />
          <p className={HINT}>
            {fits ? strings.capitalsHint : strings.capitalsUnavailable}
          </p>
        </div>
      ) : null}
    </div>
  );
}

export default function QrCodeGeneratorWidget() {
  const strings = useToolUi(QR_CODE_GENERATOR_STRINGS);
  const fmt = useToolNumber();
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [kind, setKind] = useState<QrContentKind>("doi");
  const [values, setValues] = useState<Record<QrContentKind, string>>({
    text: strings.textSample,
    url: strings.urlSample,
    doi: strings.doiSample,
  });
  const [capitals, setCapitals] = useState(false);
  const [ecc, setEcc] = useState<QrEcc>("M");
  const [mask, setMask] = useState<MaskChoice>("auto");
  const [margin, setMargin] = useState<Margin>("4");
  const [pngTarget, setPngTarget] = useState<PngTarget>("600");
  const [dark, setDark] = useState("#000000");
  const [light, setLight] = useState("#ffffff");
  const [failed, setFailed] = useState(false);

  const payload = qrPayload(kind, values[kind], capitals);
  const qr = payload.ok
    ? encodeText(payload.text, ecc, {
        mask: mask === "auto" ? -1 : Number(mask),
      })
    : null;
  const quiet = Number(margin);
  const total = (qr?.size ?? 21) + quiet * 2;
  const pixelsPerModule = (target: string) =>
    Math.max(1, Math.round(Number(target) / total));
  const scale = pixelsPerModule(pngTarget);
  const colors = checkColors(dark, light);

  async function savePng() {
    if (!svgRef.current) {
      return;
    }
    try {
      setFailed(false);
      await downloadPng(`${strings.fileName}.png`, svgRef.current, {
        scale,
        background: light,
      });
    } catch {
      setFailed(true);
    }
  }

  let problem: string | null = null;
  if (!payload.ok) {
    problem = payload.problem === "badDoi" ? strings.doiInvalid : strings.empty;
  } else if (!qr) {
    problem = t(strings.tooLong, { level: ecc });
  }

  return (
    <>
      <section className="space-y-4">
        <PillGroup
          label={strings.kind}
          name="qr-kind"
          onChange={setKind}
          options={[
            { value: "doi", label: strings.kindDoi },
            { value: "url", label: strings.kindUrl },
            { value: "text", label: strings.kindText },
          ]}
          value={kind}
        />
        <ContentFields
          capitals={capitals}
          kind={kind}
          onCapitals={setCapitals}
          onValue={(which, value) => setValues({ ...values, [which]: value })}
          strings={strings}
          values={values}
        />
        {payload.ok ? (
          <p className="break-all text-sm">
            <span className={LABEL}>{strings.holds}</span>{" "}
            <code className="font-mono text-foreground">{payload.text}</code>
          </p>
        ) : null}
      </section>

      <section className="grid gap-4 sm:grid-cols-2">
        <SelectField
          hint={strings.eccHint}
          id="qr-ecc"
          label={strings.ecc}
          onChange={setEcc}
          options={QR_ECC_LEVELS.map((level) => ({
            value: level,
            label: strings[ECC_LABELS[level]],
          }))}
          value={ecc}
        />
        <SelectField
          id="qr-mask"
          label={strings.mask}
          onChange={setMask}
          options={MASKS.map((value) => ({
            value,
            label:
              value === "auto"
                ? strings.maskAuto
                : t(strings.maskNumber, { number: value }),
          }))}
          value={mask}
        />
        <SelectField
          hint={strings.marginHint}
          id="qr-margin"
          label={strings.margin}
          onChange={setMargin}
          options={MARGINS.map((value) => ({
            value,
            label: fmt(Number(value)),
          }))}
          value={margin}
        />
        <SelectField
          id="qr-png"
          label={strings.pngSize}
          onChange={setPngTarget}
          options={PNG_TARGETS.map((value) => ({
            value,
            label: t(strings.pngOption, {
              size: fmt(pixelsPerModule(value) * total),
            }),
          }))}
          value={pngTarget}
        />
        <div className="flex flex-wrap gap-6">
          <label
            className={`${LABEL} flex items-center gap-2`}
            htmlFor="qr-dark"
          >
            <input
              className="h-9 w-12 cursor-pointer rounded border border-border bg-card"
              id="qr-dark"
              onChange={(event) => setDark(event.target.value)}
              type="color"
              value={dark}
            />
            {strings.dark}
          </label>
          <label
            className={`${LABEL} flex items-center gap-2`}
            htmlFor="qr-light"
          >
            <input
              className="h-9 w-12 cursor-pointer rounded border border-border bg-card"
              id="qr-light"
              onChange={(event) => setLight(event.target.value)}
              type="color"
              value={light}
            />
            {strings.light}
          </label>
        </div>
      </section>
      {quiet < STANDARD_QUIET_ZONE ? (
        <Notice>{strings.marginWarning}</Notice>
      ) : null}
      {colors.low ? (
        <ErrorNotice>
          {t(strings.lowContrast, {
            ratio: fmt(colors.ratio, { maximumFractionDigits: 1 }),
          })}
        </ErrorNotice>
      ) : null}
      {colors.inverted ? <ErrorNotice>{strings.inverted}</ErrorNotice> : null}

      <section aria-live="polite" className={`${CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.result}</h2>
        {problem || !qr || !payload.ok ? (
          <Notice>{problem}</Notice>
        ) : (
          <>
            <div className="flex justify-center rounded-lg border border-border bg-white p-3">
              <svg
                aria-label={t(strings.imageLabel, { payload: payload.text })}
                className="h-auto w-64 max-w-full sm:w-72"
                height={total * scale}
                ref={svgRef}
                role="img"
                shapeRendering="crispEdges"
                viewBox={`0 0 ${total} ${total}`}
                width={total * scale}
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect fill={light} height={total} width={total} />
                <path d={qrPath(qr.modules, quiet)} fill={dark} />
              </svg>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                className={BUTTON_SECONDARY}
                onClick={() =>
                  svgRef.current &&
                  downloadSvg(`${strings.fileName}.svg`, svgRef.current)
                }
                type="button"
              >
                {strings.downloadSvg}
              </button>
              <button
                className={BUTTON_SECONDARY}
                onClick={savePng}
                type="button"
              >
                {strings.downloadPng}
              </button>
            </div>
            {failed ? <ErrorNotice>{strings.exportFailed}</ErrorNotice> : null}
            <p className={HINT}>{strings.scanNote}</p>
            <QrDetails
              chosenEcc={ecc}
              qr={qr}
              strings={strings}
              text={payload.text}
            />
          </>
        )}
      </section>
    </>
  );
}
